/**
 * Helpers for the daily-revenue chart on the admin monthly billing page.
 *
 * The /billing/monthly-summary rows are aggregated per manager + ride + month,
 * and each row carries a `daily_breakdown` of [{ date, amount }] for its own
 * month. These helpers fold those breakdowns into one series per calendar day.
 */

function daysInMonth(year, month) {
  return new Date(year, month, 0).getDate();
}

/**
 * One entry per calendar day of the month, zero-filled, so the chart keeps the
 * same shape all month long.
 * @returns {Array<{day: number, date: string, amount: number}>}
 */
function buildDailySeries(rows, year, month) {
  const totals = {};
  for (const row of rows || []) {
    for (const entry of (row && row.daily_breakdown) || []) {
      if (!entry || !entry.date) continue;
      totals[entry.date] = (totals[entry.date] || 0) + Number(entry.amount || 0);
    }
  }

  const series = [];
  const lastDay = daysInMonth(year, month);
  for (let day = 1; day <= lastDay; day += 1) {
    const date = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    series.push({ day, date, amount: round2(totals[date] || 0) });
  }
  return series;
}

/**
 * Days of the month that have already happened — the denominator for a daily
 * average. A month in progress must not be divided by days that haven't come.
 */
function elapsedDaysInMonth(year, month, now = new Date()) {
  if (now.getFullYear() === year && now.getMonth() + 1 === month) {
    return Math.min(now.getDate(), daysInMonth(year, month));
  }
  return daysInMonth(year, month);
}

/**
 * The y scale, rounded out to a clean 1/2/5 step. The ticks sit on multiples of
 * that step rather than on max/4, so they read as 0 / 500 / 1,000 / 1,500 and
 * never as 0 / 375 / 750 / 1,125.
 * @returns {{max: number, step: number, ticks: number[]}}
 */
function niceAxisScale(peak) {
  if (!peak || peak <= 0) return { max: 10, step: 5, ticks: [0, 5, 10] };

  const rough = peak / 4;
  const magnitude = 10 ** Math.floor(Math.log10(rough));
  const normalized = rough / magnitude;
  let step = 10;
  if (normalized <= 1) step = 1;
  else if (normalized <= 2) step = 2;
  else if (normalized <= 5) step = 5;
  step *= magnitude;

  const max = round2(step * Math.ceil(peak / step));
  const ticks = [];
  for (let value = 0; value <= max + step / 2; value += step) {
    ticks.push(round2(value));
  }
  return { max, step, ticks };
}

function round2(value) {
  return Math.round(value * 100) / 100;
}

module.exports = {
  daysInMonth,
  buildDailySeries,
  elapsedDaysInMonth,
  niceAxisScale,
};
